import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  Group,
} from '@/components/_common';
import { InvoiceGeneralFormValues } from '@/core/domain/invoice/invoice/Invoice.types';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import ExpiredCertificateAlert from '@/pages/FiscalPages/InvoicePage/ExpiredCertificateAlert';
import { Card, Col, Empty, Form, Row, Space, Spin } from 'antd';
import React, { useEffect } from 'react';
import { FaFileInvoice } from 'react-icons/fa';

const invoceTypes = [
  { value: InvoiceType.NFE, label: 'NF-E', propName: 'issueNfe' },
  { value: InvoiceType.NFC, label: 'NFC-E', propName: 'issueNfc' },
  { value: InvoiceType.NFS, label: 'NFS-E', propName: 'issueNfs' },
];

function hasBoolean(value: boolean | undefined) {
  return typeof value === 'boolean' ? String(value) : undefined;
}

const InvoiceStepType: React.FC = () => {
  const form = Form.useFormInstance<InvoiceGeneralFormValues>();
  const companyId = Form.useWatch('companyId', form);
  const invoiceType = Form.useWatch('invoiceType', form);

  const ruleRequired = useDefaultEmptyFieldError();

  const { data: settings, isLoading } = useInvoiceSettingsByCompany(companyId);

  useEffect(() => {
    if (settings) {
      form.setFieldsValue({
        simpleNational: hasBoolean(settings.simpleNational),
        culturalIncentivator: hasBoolean(settings.culturalIncentivator),
        natureOfOperation:
          invoiceType === InvoiceType.NFE
            ? settings.configNfe?.natureOfOperationDefault
            : settings.configNfc?.natureOfOperationDefault,
        taxCode:
          invoiceType === InvoiceType.NFS
            ? settings.configNfs?.taxCodeDefault
            : undefined,
      });
    }
  }, [settings, invoiceType]);

  function resetFileds() {
    form.resetFields([
      'situation',
      'integrationId',
      'serie',
      'taxationNature',
      'numberRps',
      'serviceItemList',
      'natureOfOperation',
      'emissionGoal',
      'documentType',
      'finalConsumer',
      'operationDestination',
      'departureDate',
      'emissionType',
      'faceToFace',
      ['recipient', 'recipientType'],
      ['recipient', 'customer'],
      ['recipient', 'customerId'],
      ['recipient', 'supplier'],
      ['recipient', 'supplierId'],
      ['recipient', 'insurer'],
      ['recipient', 'insurerId'],
    ]);
  }

  return (
    <>
      <p>
        <strong>Selecione a empresa e o tipo de nota</strong>
      </p>

      <FloatingFormItem name="companyId" label="Empresa" rules={[ruleRequired]}>
        <CompaniesInfiniteSelectField />
      </FloatingFormItem>

      {!!companyId && (
        <Col span={24}>
          <ExpiredCertificateAlert companyId={companyId} />
        </Col>
      )}

      {!!isLoading && (
        <Group position="center" style={{ marginTop: 24, height: 100 }}>
          <Spin size="large" />
        </Group>
      )}

      {!companyId && !isLoading && (
        <Empty description="Selecione uma empresa" />
      )}

      {!!companyId &&
        !isLoading &&
        !settings?.issueNfc &&
        !settings?.issueNfe &&
        !settings?.issueNfs && (
          <Empty description="Não há tipos de nota configurado para esta empresa" />
        )}

      {!!companyId &&
        !isLoading &&
        (!!settings?.issueNfc ||
          !!settings?.issueNfe ||
          !!settings?.issueNfs) && (
          <>
            <Form.Item
              shouldUpdate={(prev, cur) => prev.invoiceType !== cur.invoiceType}
              style={{ marginBottom: 0, marginTop: 24 }}
            >
              {({ getFieldValue, setFieldValue }) => (
                <Row gutter={[16, 16]} justify="start">
                  {invoceTypes
                    .filter(
                      (item) =>
                        settings[item.propName as keyof typeof settings],
                    )
                    .map((item) => (
                      <Col span={24} md={8} key={item.value}>
                        <Card
                          className="invoice-create-steps-card"
                          hoverable
                          onClick={() => {
                            resetFileds();
                            setFieldValue('invoiceType', item.value);
                          }}
                          data-active={
                            getFieldValue('invoiceType') === item.value
                          }
                        >
                          <Space
                            direction="vertical"
                            size="small"
                            align="center"
                          >
                            <FaFileInvoice size={32} />{' '}
                            <span>{item.label}</span>
                          </Space>
                        </Card>
                      </Col>
                    ))}
                </Row>
              )}
            </Form.Item>
          </>
        )}

      <Form.Item
        className="remove-form-control"
        name="invoiceType"
        rules={[
          {
            required: true,
            message: (
              <Group position="center">
                <span>Selecione um tipo de nota</span>
              </Group>
            ),
          },
        ]}
      />
    </>
  );
};

export default InvoiceStepType;
